import type { Country } from '@bb/domain/dist/types';
import type {
  IOnboardingGetProfileResponse,
  IOnboardingGetOrgProfileResponse,
} from '@/app-package';
import type {
  CommonStep,
  OrgOnboardingStep,
  StageName,
  UserOnboardingStep,
} from './constants';

export type UserRemoteOnboardingProfile = IOnboardingGetProfileResponse;

export type UserLocalOnboardingProfile = IOnboardingGetProfileResponse;

export type OrgRemoteOnboardingProfile = IOnboardingGetOrgProfileResponse;

export type OrgLocalOnboardingProfile = OrgRemoteOnboardingProfile & {
  primaryRole?: PrimaryRole;
  isLicensed?: boolean;
};

export type WizardType = 'user' | 'org';

/**
 * * `default` - Allow user to enter their member number
 * * `verificationSent` - Indicate that a verification email has been sent and prompt resending
 * * `contactOffice` - User might need assistance from their office since they are
 * disabled (or already claimed in user onboarding)
 * * `contactSales` - Allow user to contact sales team as their account cannot be found
 * * `salesFormSubmitted` - Indicate that an email has been sent to the sales team
 * * `wrongOrg` - Indicate that the user is not tied to the org they're claiming (only for org onboarding)
 */
export type AgentVerificationPageState =
  | 'default'
  | 'verificationSent'
  | 'contactOffice'
  | 'contactSales'
  | 'salesFormSubmitted'
  | 'wrongOrg';

export type Steps = UserOnboardingStep | OrgOnboardingStep | CommonStep;

// Might want to add claimHash to this instead of using it as a url parameter
export type OrgOnboardingSearchParams =
  | 'verifyHash'
  | 'approved'
  | 'isLicensed'
  | 'isClaimed'
  | 'email'
  | 'name'
  | 'userId';

export type OnboardingWizard<T extends UserOnboardingStep | OrgOnboardingStep> =
  Record<T, string>;

export type PortalAssociation = {
  id: string;
  useAppointmentDesk: boolean;
  useLiveConciergePlus: boolean;
  conciergeEnabled: boolean;
};

/**
 *
 * These types exist as placeholders until Team Kusa creates these types
 *
 */

// #region Placeholder Types

export type PortalWithAssociationDataConfig = {
  id?: string | null;
  name?: string | null;
  country?: Country | null;
  adminTrainingLink?: string | null;
  agentTrainingLink?: string | null;
  helpCenterLink?: string | null;
  contactUsLink?: string | null;
  /** @deprecated instead use OrgLaunchSetting from Automaton */
  launchDates?: string[] | null;
  overrideLabel?: string | null;
  isLandingPageActive?: boolean;
  plan?: string | null;
  globalTaxPercentage?: number | null;
  associations?: PortalAssociation[] | null;
};

export type Stage = {
  id: number;
  createdAt: Date;
  updatedAt: Date;
  onboardingId: number;
  name: StageName;
};

export type OnboardingSubmitter = { email: string };

export type Onboarding = {
  id: string;
  launchDate?: string;
  orgId: string;
  currentStage: Stage;
  stages: any[];
  lastOnboardingEmailDate?: string;
  emailSent?: string;
  submitters?: OnboardingSubmitter[];
};

export enum PrimaryRole {
  Administrator = 'Administrator',
  OfficeManager = 'Office Manager',
  BrokerageOwner = 'Brokerage Owner',
  BoR = 'BoR',
}

// #endregion Placeholder Types
